import notification from "antd/es/notification";
import browserLocale from "browser-locale";
import { differenceInCalendarDays } from "date-fns";
import { Task } from "redux-saga";
import { all, call, fork, join, put, race, select, take, takeLeading } from "redux-saga/effects";

import { OrganizationSetting } from "@mapmycustomers/shared";
import DistanceUnit from "@mapmycustomers/shared/enum/DistanceUnit";
import { EntityType, MapEntity } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import PersistentLastUsedLayoutData from "@mapmycustomers/shared/types/persistent/PersistentLastUsedLayoutData";
import { SandboxAccessTokenData } from "@mapmycustomers/shared/types/Sandbox";
import Setting from "@mapmycustomers/shared/types/Setting";
import { TokenHandoffCodeData } from "@mapmycustomers/shared/types/TokenHandoff";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import UserSetting from "@app/enum/UserSetting";
import { setUserIds } from "@app/scene/reports/store/actions";
import { getUserIds } from "@app/scene/reports/store/selectors";
import reduxStore from "@app/store";
import { fetchAnnouncements, suppressAnnouncements } from "@app/store/announcements/actions";
import { login, logout } from "@app/store/auth/actions";
import { fetchBoundaryLegends } from "@app/store/boundaryLegends/actions";
import { clearChatConversation } from "@app/store/chat";
import { initializeCumul } from "@app/store/cumul/actions";
import { fetchAllCustomFields } from "@app/store/customFields/actions";
import { handleError } from "@app/store/errors/actions";
import { fetchAllGroups } from "@app/store/groups/actions";
import {
  getLastUsedLayout,
  getMe,
  getOrganizationId,
  getOrganizationSettingValue,
  getUserSetting,
  getUserSettingValue,
  isCumulEnabled,
  isCurrentUserMember,
  isCurrentUserOwner,
} from "@app/store/iam";
import {
  fetchGeocodingLimit,
  fetchMe,
  fetchOrgSandbox,
  fetchPermissions,
  fetchPlans,
  findMe,
  getUserCountry,
  setOrganizationSize,
  updateSetting,
} from "@app/store/iam/actions";
import { isOwner } from "@app/store/iam/util";
import { fetchAllLayouts } from "@app/store/layout/actions";
import { changeLocale } from "@app/store/locale/actions";
import { getUsers } from "@app/store/members";
import { fetchRoles, fetchTeams, fetchUsers } from "@app/store/members/actions";
import { onFetchRoles, onFetchUsers } from "@app/store/members/sagas";
import { fetchNotificationsUnreadTotal } from "@app/store/notification/actions";
import { initializeNylas } from "@app/store/nylas/actions";
import { refreshGuidesStatus } from "@app/store/onboarding/actions";
import { fetchPinLegends } from "@app/store/pinLegends/actions";
import { fetchReferenceData } from "@app/store/referenceData/actions";
import { fetchAllSavedFilters } from "@app/store/savedFilters/actions";
import { applyDefaultMapFilterOnSignIn } from "@app/store/savedFilters/sagas";
import { fetchSavedAddresses } from "@app/store/savedLocations/actions";
import { fetchAllSchemas } from "@app/store/schema/actions";
import Iam from "@app/types/Iam";
import analyticsService from "@app/util/analytic/AnalyticsService";
import announcementsSettings from "@app/util/announcements/announcementsSettings";
import { getLocalTimeZone } from "@app/util/dates";
import isValidDate from "@app/util/isValidDate";
import {
  activityLayoutModel,
  companyLayoutModel,
  dealLayoutModel,
  personLayoutModel,
} from "@app/util/layout/impl";
import loggingService from "@app/util/logging";
import { ensureGoogleMapsApiIsLoaded } from "@app/util/mapping";
import { parseApiDate } from "@app/util/parsers";
import getNotificationNodeWithAction from "@app/util/ui/getNotificationNodeWithAction";

import { fetchActivityTypes } from "../activity/actions";
import { callApi } from "../api/callApi";
import { fetchAllFunnels } from "../deal/actions";
import { fetchAllFileTags } from "../fileTags/actions";
import { buildRecordsPreviewConfiguration } from "../recordPreview/sagas";
import { initializeUserColors } from "../userColor/actions";

import {
  applyDefaultColumns,
  applyFeaturesOverride,
  syncFieldModelFilters,
  syncFieldModelSchemas,
  syncRoleGatedFieldFeatures,
} from "./accessSetup";
import {
  fetchSandboxAccessToken,
  fetchTokenHandoffCode,
  initializeApp,
  reinitializeApp,
} from "./actions";

// The failure action a fetch saga puts; its payload is the error, when it has one
interface FetchFailure {
  payload?: unknown;
}

const languageToRegion: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
};

export function* onInitialize() {
  try {
    // since we added code which requires google api into the navbar (starting from
    // create company modal), we need to load google apis from the beginning.
    // We only wait for the result of this action in the very end of this saga.
    const googleMapsLoadingTask: Task = yield fork(ensureGoogleMapsApiIsLoaded);

    // call fetch* actions
    yield put(fetchMe.request());
    yield put(findMe.request()); // not waiting for this one to complete or fail

    // now wait till all succeed or any fails, whichever happens first
    const { failure } = yield race({
      failure: take([fetchMe.failure]),
      success: all([take(fetchMe.success)]),
    });

    // fail app initialization if any of fetch actions failed, and succeed otherwise
    if (failure) {
      yield put(initializeApp.failure());
      yield put(handleError({ error: failure.payload }));
      return;
    }

    const me: Iam = yield select(getMe);

    const userSettingLanguage: Setting | undefined = yield select(getUserSetting, "language");
    const languageSettingValue: null | string = userSettingLanguage?.value ?? null;
    const locale: string = languageSettingValue || (yield call(browserLocale));
    yield put(changeLocale.request(locale));
    yield race([take(changeLocale.success), take(changeLocale.failure)]);

    // Update "region" user setting to keep it in sync with the actual region user uses
    // Especially useful when Language is set to "auto-detect"
    const userSettingRegion: Setting | undefined = yield select(getUserSetting, "region");
    if (userSettingRegion && !localSettings.wasSkeletonKeyUsed()) {
      const region =
        (languageSettingValue ? languageToRegion[languageSettingValue] : undefined) ??
        (yield call(browserLocale));
      if (region !== userSettingRegion.value) {
        loggingService.debug(
          `Update region setting to "${region}" (previous value: "${userSettingRegion.value}")`
        );
        const setting = { ...userSettingRegion, value: region };
        yield put(updateSetting.request({ orgId: me.organization.id, setting, userId: me.id }));
      }
    }

    const userSettingTimezone: Setting | undefined = yield select(getUserSetting, "timezone");
    if (userSettingTimezone && !localSettings.wasSkeletonKeyUsed()) {
      const timezone = getLocalTimeZone();
      if (timezone && timezone !== userSettingTimezone.value) {
        const setting = { ...userSettingTimezone, value: timezone };
        yield put(updateSetting.request({ orgId: me.organization.id, setting, userId: me.id }));
      }
    }

    yield put(initializeUserColors());

    yield call(analyticsService.identify, {
      ...me,
      "sign-upSource": localSettings.getSignupCompleted() ? "Web" : undefined,
    });
    yield call(analyticsService.group, me);
    localSettings.setSignupCompleted(false);

    const distanceUnit: DistanceUnit =
      (yield select(getUserSettingValue, UserSetting.DISTANCE_UNIT)) ??
      (yield select(getOrganizationSettingValue, OrganizationSetting.DISTANCE_UNIT)) ??
      DistanceUnit.MILE;
    localSettings.setSiUnitsUsage(distanceUnit === DistanceUnit.KM);

    const currency: string =
      (yield select(getOrganizationSettingValue, OrganizationSetting.CURRENCY)) ?? "USD";
    localSettings.setCurrencyCode(currency);

    const uidExport: boolean =
      (yield select(getOrganizationSettingValue, OrganizationSetting.EXPORT_UID)) ?? false;
    localSettings.setUidExportSetting(uidExport);

    const prevUserId = localSettings.getUserId();
    // means that another user logged in, from this, we should clear view settings
    if (prevUserId !== me.id) {
      localSettings.resetViewSettings();
      // The chat thread lives in sessionStorage, and signOut is a same-tab navigation, which
      // does not clear it — so without this, the next rep to log in on this tab inherits the
      // previous rep's conversation.
      yield put(clearChatConversation());
      localSettings.setUserId(me.id);
    }

    // Loaded once a session: none of it depends on the user's access
    const startupDataTask: Task = yield fork(loadStartupData);
    // Not in the race below: auto-running guides wait for it, the app never does.
    // A tab an announcement CTA opened fetches nothing and lets the guides start at once.
    const announcementsOff: boolean = yield call(announcementsSettings.areAnnouncementsOffInTab);
    yield put(announcementsOff ? suppressAnnouncements() : fetchAnnouncements.request());
    yield put(fetchSavedAddresses.request());

    const accessFailure: FetchFailure | undefined = yield call(loadAccessDependentData, me);
    const startupFailure: FetchFailure | undefined = yield join(startupDataTask);

    // fail app initialization if any of fetch actions failed, and succeed otherwise
    const failure2 = accessFailure ?? startupFailure;
    if (failure2) {
      yield put(initializeApp.failure());
      yield put(handleError({ error: failure2.payload }));
      return;
    }

    // initialize layouts
    const recentLayouts: PersistentLastUsedLayoutData | undefined = yield select(getLastUsedLayout);
    if (recentLayouts?.[EntityType.ACTIVITY]) {
      activityLayoutModel.setRecentLayoutId(recentLayouts[EntityType.ACTIVITY]!);
    }
    if (recentLayouts?.[EntityType.COMPANY]) {
      companyLayoutModel.setRecentLayoutId(recentLayouts[EntityType.COMPANY]!);
    }
    if (recentLayouts?.[EntityType.DEAL]) {
      dealLayoutModel.setRecentLayoutId(recentLayouts[EntityType.DEAL]!);
    }
    if (recentLayouts?.[EntityType.PERSON]) {
      personLayoutModel.setRecentLayoutId(recentLayouts[EntityType.PERSON]!);
    }

    // https://mapmycustomers.atlassian.net/browse/TART-22746
    // https://mapmycustomers.atlassian.net/browse/TART-25493
    if (me.organization.id === 31104 || me.organization.id === 33062) {
      yield put(setOrganizationSize("medium"));
    } else {
      const pinsResponse: ListResponse<MapEntity> = yield callApi("fetchPins", me.organization.id, {
        $filters: { entities: { accounts: {}, contacts: {}, deals: {} } },
        $limit: 0,
      });
      // a threshold after which we consider organization big
      if (pinsResponse.accessible > 12000) {
        yield put(setOrganizationSize("big"));
      }
    }

    const isCumulEnabledForOrg: boolean = yield select(isCumulEnabled);

    // Initializing cumul here in order to get cumul auth response and
    // dashboard list in advance to redirect reports directly to first pinned dashboard
    // and to show pinned dashboards name in menu which will be fetched from dashboard's list
    if (isCumulEnabledForOrg) {
      yield put(initializeCumul.request());
    }

    // now let's ensure google maps loading task (which was executed in parallel) is also complete
    yield join(googleMapsLoadingTask);

    // before success: loggedIn can fire twice per sign-in, and each run navigates once init succeeds
    yield call(applyDefaultMapFilterOnSignIn);

    yield put(initializeApp.success(me));

    // not waiting for this request to complete
    yield put(fetchNotificationsUnreadTotal.request());

    // not waiting for this one to complete or fail
    yield put(fetchOrgSandbox.request());

    // not waiting for this one to complete or fail
    yield put(getUserCountry.request());

    // not waiting for this one to complete or fail
    yield put(fetchPlans.request());

    // Let's check if password expires soon
    const passwordExpirationDate = parseApiDate(me.passwordExpiresAt);
    const expiresInDays = differenceInCalendarDays(passwordExpirationDate, Date.now());
    if (isValidDate(passwordExpirationDate) && expiresInDays <= 30 && !me.ssoLogin) {
      const intl = i18nService.getIntl();
      if (intl) {
        notification.warning({
          duration: 0, // never hide automatically
          key: "passwordExpirationWarning",
          message: getNotificationNodeWithAction(
            intl.formatMessage(
              {
                id: "app.auth.passwordExpirationWarning",
                defaultMessage:
                  "Your password is expiring in {count} {count, plural, one {day} other {days}}",
                description: "A message of the password expiration warning",
              },
              {
                count: expiresInDays,
              }
            ),
            intl.formatMessage({
              id: "app.auth.passwordExpirationWarning.action",
              defaultMessage: "Update Now",
              description: "An action of the password expiration warning",
            }),
            () => {
              notification.close("passwordExpirationWarning");
              reduxStore.history.push(
                `${Path.SETTINGS}/${SettingPath.PERSONAL_PROFILE}?changePassword`
              );
            }
          ),
        });
      }
    }
  } catch (error) {
    yield put(initializeApp.failure());
    yield put(handleError({ error }));
  }
}

// Reports show a rep their own data, and everyone else all the users they may see. A user the
// selection may no longer include, e.g. an owner after the viewer's demotion, is dropped from it.
export function* syncReportUserIds(me: Iam) {
  const allUsers: User[] = yield select(getUsers);
  const currentUserMember: boolean = yield select(isCurrentUserMember);
  const currentUserOwner: boolean = yield select(isCurrentUserOwner);
  const allowedUserIds = currentUserMember
    ? [me.id]
    : allUsers.filter((user) => (isOwner(user) ? currentUserOwner : true)).map(({ id }) => id);

  const reportUserIds: undefined | User["id"][] = yield select(getUserIds);
  const keptUserIds = (reportUserIds ?? []).filter((userId) => allowedUserIds.includes(userId));
  if (keptUserIds.length && keptUserIds.length === reportUserIds?.length) {
    return;
  }
  yield put(setUserIds(keptUserIds.length ? keptUserIds : allowedUserIds));
}

function* loadStartupData() {
  // these two don't really require IAM data, but would fail if token is outdated, so let's call them
  // after having IAM to guarantee we use a fresh token
  yield put(fetchReferenceData.request());
  yield put(initializeNylas.request());
  yield put(refreshGuidesStatus.request());

  const { failure } = yield race({
    failure: take([
      fetchReferenceData.failure,
      initializeNylas.failure,
      refreshGuidesStatus.failure,
    ]),
    success: all([
      take(fetchReferenceData.success),
      take(initializeNylas.success),
      take(refreshGuidesStatus.success),
    ]),
  });
  return failure as FetchFailure | undefined;
}

// What depends on the user's access, run by startup and Update this page alike. Nothing in it
// empties the store while it loads. Returns the failure of the first fetch that fails.
export function* loadAccessDependentData(me: Iam) {
  yield call(applyFeaturesOverride);
  yield call(syncFieldModelFilters);

  yield put(fetchPermissions.request(me.role.id));
  yield put(fetchAllCustomFields.request());
  yield put(fetchAllGroups.request());
  yield put(fetchAllLayouts.request());
  yield put(fetchAllSchemas.request());
  yield put(fetchAllFileTags.request());
  yield put(fetchTeams.request());
  yield put(fetchAllFunnels.request());
  yield put(fetchActivityTypes.request());
  yield put(fetchGeocodingLimit.request());
  yield put(fetchPinLegends.request());
  // need to load them in order to initialize mapViewState correctly (see getMapViewSettings);
  // forced, since the saga otherwise answers from the legends already loaded
  yield put(fetchBoundaryLegends.request({ force: true }));
  // without their request actions, which empty the roles and users while they load
  yield fork(onFetchRoles);
  yield fork(onFetchUsers);

  // now wait till all succeed or any fails, whichever happens first
  const { failure } = yield race({
    failure: take([
      fetchPermissions.failure,
      fetchAllCustomFields.failure,
      fetchAllGroups.failure,
      fetchAllSchemas.failure,
      fetchRoles.failure,
      fetchTeams.failure,
      fetchUsers.failure,
      fetchAllFunnels.failure,
      fetchActivityTypes.failure,
      fetchGeocodingLimit.failure,
      fetchPinLegends.failure,
      fetchBoundaryLegends.failure,
    ]),
    success: all([
      take(fetchPermissions.success),
      take(fetchAllCustomFields.success),
      take(fetchAllGroups.success),
      take(fetchAllSchemas.success),
      take(fetchRoles.success),
      take(fetchTeams.success),
      take(fetchUsers.success),
      take(fetchAllFunnels.success),
      take(fetchActivityTypes.success),
      take(fetchGeocodingLimit.success),
      take(fetchPinLegends.success),
      take(fetchBoundaryLegends.success),
    ]),
  });
  if (failure) {
    return failure as FetchFailure;
  }

  // We need to setSchema here rather than in fetchAllSchemas saga to ensure
  // both schemas and custom fields are loaded.
  yield call(syncFieldModelSchemas);
  yield call(syncReportUserIds, me);
  // setting default columns, should only be done after CFs are fetched
  yield call(applyDefaultColumns);
  yield call(syncRoleGatedFieldFeatures);

  // Only fetch saved filters when schemas and custom fields are fetched
  yield put(fetchAllSavedFilters.request());
  const { failure: savedFiltersFailure } = yield race({
    failure: take([fetchAllSavedFilters.failure]),
    success: all([take(fetchAllSavedFilters.success)]),
  });
  if (savedFiltersFailure) {
    return savedFiltersFailure as FetchFailure;
  }

  // initialize record preview configuration
  yield call(buildRecordsPreviewConfiguration);
  return undefined;
}

export function* onReinitialize() {
  try {
    // a plain call: fetchMe.request empties `me` while it loads, which unmounts the app
    const me: Iam = yield callApi("fetchMe");
    yield put(fetchMe.success(me));
    const failure: FetchFailure | undefined = yield call(loadAccessDependentData, me);
    if (failure) {
      yield put(reinitializeApp.failure(failure.payload));
      return;
    }
    yield call(analyticsService.identify, me);
    yield call(analyticsService.group, me);
    yield put(reinitializeApp.success(me));
  } catch (error) {
    yield put(reinitializeApp.failure(error));
  }
}

// A reinitialization must not outlive the session it loads for
function* reinitializeUntilSignOut() {
  yield race({ reinitialized: call(onReinitialize), signedOut: take([login, logout]) });
}

export function* onFetchSandboxAccessToken({
  payload: { callback },
}: ReturnType<typeof fetchSandboxAccessToken.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { accessToken, code }: SandboxAccessTokenData = yield callApi(
      "getSandboxAccessToken",
      orgId
    );
    if (callback) {
      yield call(callback, accessToken, code);
    }
    yield put(fetchSandboxAccessToken.success(accessToken));
  } catch (error) {
    yield put(fetchSandboxAccessToken.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchTokenHandoffCode({
  payload: { callback, failureCallback },
}: ReturnType<typeof fetchTokenHandoffCode>) {
  try {
    const { code }: TokenHandoffCodeData = yield callApi("createTokenHandoffCode");
    yield call(callback, code);
  } catch (error) {
    // log but no toast: the caller falls back to the token URL, so the mint
    // failure needs observability without a message the user can't act on
    yield put(handleError({ displayNotification: false, error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

export function* appSaga() {
  yield takeLeading(initializeApp.request, onInitialize);
  yield takeLeading(reinitializeApp.request, reinitializeUntilSignOut);
  yield takeLeading(fetchSandboxAccessToken.request, onFetchSandboxAccessToken);
  yield takeLeading(fetchTokenHandoffCode, onFetchTokenHandoffCode);
}
